import { usePermStore } from "./permStore"
import { useTempStore } from "./store"
import { client, type ClientInput } from "./ts-rest"
import { createId } from "@paralleldrive/cuid2"
import * as Sentry from "@sentry/react"
import posthog from "posthog-js"
import ReactGA from "react-ga4"

/**
 * Captures an error to both Sentry and PostHog for unified error tracking.
 * Use this instead of Sentry.captureException directly.
 *
 * Note: PostHog user identification is handled via posthog.identify() in dashboard.tsx,
 * so exceptions are automatically associated with the logged-in user.
 */
export function captureError(
  exception: unknown,
  context?: {
    tags?: Record<string, string>
    extra?: Record<string, unknown>
  }
) {
  // Capture to Sentry
  Sentry.captureException(exception, {
    tags: context?.tags,
    extra: context?.extra,
  })

  // Capture to PostHog (user is already identified via posthog.identify)
  posthog.captureException(exception, {
    ...context?.tags,
    ...context?.extra,
  })
}

// The demo lead capture event becomes a Google Ads conversion once it reaches
// GA4, so it is the one GA4 event that waits for the API to accept the submission
const LEAD_CAPTURE_EVENT = "submitted demo lead capture form"

// Maps Segment demo event names to GA4 snake_case event names
const ga4DemoEventMap: Record<string, string> = {
  [LEAD_CAPTURE_EVENT]: "demo_lead_form_submitted",
  "started a call": "demo_call_started",
  "completed a call": "demo_call_completed",
  "viewed call page": "demo_call_feedback_viewed",
}

// Fires a GA4 custom event for tracked demo events, including relevant non-PII properties
function trackGA4DemoEvent(eventName: string, properties?: Record<string, unknown>) {
  const ga4EventName = ga4DemoEventMap[eventName]
  if (!ga4EventName) return

  // Only fire for demo events — "submitted demo lead capture form" is inherently demo,
  // others require demoOrgId in properties to distinguish demo from non-demo calls
  const isDemoEvent = eventName.includes("demo") || properties?.demoOrgId
  if (!isDemoEvent) return

  // Build GA4 params from available properties (no PII like email)
  const ga4Params: Record<string, string | number> = {}
  if (properties?.demoOrgId) ga4Params.demo_org_id = String(properties.demoOrgId)
  if (properties?.scenarioId) ga4Params.scenario_id = String(properties.scenarioId)
  if (properties?.callId) ga4Params.call_id = String(properties.callId)
  if (properties?.durationMinutes) ga4Params.duration_minutes = Number(properties.durationMinutes)
  if (properties?.difficultyLevel) ga4Params.difficulty_level = String(properties.difficultyLevel)
  // The post-call demo form accepts personal addresses, so leads are tagged
  // for Google Ads to tell them apart from work-email conversions
  if (typeof properties?.isPersonalEmail === "boolean") {
    ga4Params.is_personal_email = String(properties.isPersonalEmail)
  }

  ReactGA.event(ga4EventName, ga4Params)
}

// Detects an automated Playwright run so its events can be flagged and dropped
// server-side before reaching Segment/PostHog (keeps test traffic out of the
// #posthog-demo Slack channel). The flag is set by the e2e suite via a
// localStorage entry or a `?e2e=true` marker, which we persist to localStorage
// so it survives demo step navigation that drops query params.
function isAutomatedTestRun() {
  try {
    if (localStorage.getItem("e2eTest") === "true") return true
    if (new URLSearchParams(window.location.search).get("e2e") === "true") {
      localStorage.setItem("e2eTest", "true")
      return true
    }
  } catch {
    // localStorage / URL access can throw in some environments — treat as non-test
  }
  return false
}

// Identifies a demo visitor in PostHog by their email so their prior anonymous
// website + in-app activity merges onto a single person — mirroring the
// app-login identify in dashboard.tsx. Without this, the demo email only lands
// as a person property on a separate server-generated anonymous id and is never
// stitched to the visitor's anonymous browser session, so a demo lead's website
// activity isn't identifiable as them.
export function identifyDemoVisitor({
  email,
  demoOrgId,
}: {
  email: string
  demoOrgId?: string
}) {
  // `email` is typed as string, but its callers source it from an unchecked
  // JSON.parse of localStorage, so guard at runtime before calling string methods
  // — a corrupt/legacy non-string value would otherwise throw here.
  if (typeof email !== "string") return

  // Normalise so casing/whitespace differences don't fragment a lead across
  // multiple PostHog persons (the email is the distinct id)
  const normalizedEmail = email.trim().toLowerCase()
  if (!normalizedEmail) return

  // Skip emulated users, and skip when a real app user is logged in — we never
  // want to overwrite an authenticated user's PostHog identity (set via
  // posthog.identify(user.id) on login) with a stale demo email
  const { emulateUserId, userId } = usePermStore.getState()
  if (emulateUserId || userId) return

  // Skip automated test runs so demo test traffic stays out of PostHog
  if (isAutomatedTestRun()) return

  // posthog-js is not initialised under Playwright/webdriver, and the app-login
  // identify also skips development — mirror both guards so behaviour matches
  if (navigator.webdriver) return
  if (import.meta.env.VITE_APP_ENV === "development") return

  // Merge the anonymous browser person (which carries the visitor's website
  // pageviews) into a person keyed by email
  posthog.identify(normalizedEmail, {
    email: normalizedEmail,
    ...(demoOrgId ? { demoOrgId } : {}),
  })
}

// Fallback anonymous id for visitors whose localStorage is inaccessible, kept
// for the lifetime of the page load so their events share one identity
let inMemoryAnonymousId: string | undefined

// Posts a tracking event to the API. Analytics delivery is best-effort: the
// request can fail for reasons outside our control (offline network, ad
// blockers filtering the "trackEvent" URL, page unload aborting the fetch), and
// most callers fire-and-forget, so a rejection here would surface as an
// unhandled promise rejection rather than be caught by anyone. Callers whose
// event carries a real user submission (lead capture, info requests) pass
// throwOnFailure so they can keep surfacing delivery failure to the user.
// Resolves true when the API accepted the event and false when a failure was
// swallowed, so trackEvent can hold back work that must only happen for an
// accepted submission (the GA4 lead conversion).
async function postTrackEvent({
  body,
  throwOnFailure,
}: {
  body: ClientInput<"trackEvent">["body"]
  throwOnFailure?: boolean
}) {
  try {
    await client.trackEvent.mutate({ body })
    return true
  } catch (error) {
    if (throwOnFailure) {
      throw error
    } else {
      // Losing a single analytics event is acceptable; a user-facing error is
      // not. Keep a debug trace so systemic delivery breakage stays diagnosable.
      console.debug("trackEvent delivery failed", error)
      return false
    }
  }
}

export async function trackEvent(
  obj: Pick<ClientInput<"trackEvent">["body"], "eventName" | "properties"> & {
    userId?: number
    // Set when the event body is the sole transport for user-submitted data and
    // the caller shows its own error state on delivery failure
    throwOnFailure?: boolean
  }
) {
  const { emulateUserId } = usePermStore.getState()
  if (emulateUserId) return

  // Stamp automated test traffic so the API can drop it before any analytics
  // destination. We still POST below so e2e assertions on the payload pass.
  const isAutomatedTest = isAutomatedTestRun()
  const properties = isAutomatedTest
    ? { ...obj.properties, isAutomatedTest: true }
    : obj.properties

  // Fire GA4 custom events for demo events alongside Segment (skip for test
  // runs). The lead capture event is the exception: its GA4 counterpart
  // `demo_lead_form_submitted` is a Google Ads conversion, so it waits until
  // the API has accepted the submission (below) instead of firing for a POST
  // that may still fail.
  const ga4Properties = properties as Record<string, unknown> | undefined
  const deferGA4UntilDelivered = obj.eventName === LEAD_CAPTURE_EVENT
  if (!isAutomatedTest && !deferGA4UntilDelivered) {
    trackGA4DemoEvent(obj.eventName, ga4Properties)
  }

  // Whether the API accepted the event; stays false when delivery failed and
  // the caller did not ask for the failure to be thrown
  let delivered = false

  if (obj.userId) {
    const body: ClientInput<"trackEvent">["body"] = {
      userType: "user",
      userId: obj.userId,
      eventName: obj.eventName,
      properties,
    }
    delivered = await postTrackEvent({ body, throwOnFailure: obj.throwOnFailure })
  } else {
    // localStorage access can throw when storage is denied (blocked cookies,
    // sandboxed embeds) — fall back to a non-persisted anonymous id so the
    // event still sends instead of rejecting
    let anonymousId: string | undefined
    let foundDemoEmail: string | null = null
    try {
      anonymousId = localStorage.getItem("anonymousId") ?? undefined
      if (!anonymousId) {
        anonymousId = `anonymous-${createId()}`
        localStorage.setItem("anonymousId", anonymousId)
      }
      foundDemoEmail = localStorage.getItem("demoEmail")
    } catch {
      // Storage denied — anonymousId is filled in below if still unset
    }
    if (!anonymousId) {
      // Cache the fallback id so a storage-denied visitor keeps one anonymous
      // identity per page load instead of fragmenting one profile per event
      inMemoryAnonymousId = inMemoryAnonymousId ?? `anonymous-${createId()}`
      anonymousId = inMemoryAnonymousId
    }

    let demoEmail: string | undefined
    if (foundDemoEmail) {
      try {
        demoEmail = JSON.parse(foundDemoEmail)
      } catch {
        demoEmail = foundDemoEmail
      }
    }

    // Keep PostHog identity in sync for returning demo visitors, but ONLY for
    // demo events (gated on demoOrgId). Non-demo anonymous events — e.g.
    // root.tsx "viewed a page" or the magic-link login flow — must never
    // identify the browser as a stale demo email. Source the email from the
    // event first (a shared call link knows it only there) then fall back to the
    // stored demoEmail. identifyDemoVisitor normalises and guards
    // test/emulated/logged-in users.
    if (properties?.demoOrgId) {
      const eventEmail =
        typeof properties.email === "string" ? properties.email : undefined
      const demoIdentityEmail = eventEmail || demoEmail
      if (demoIdentityEmail) {
        identifyDemoVisitor({
          email: demoIdentityEmail,
          demoOrgId: String(properties.demoOrgId),
        })
      }
    }

    const body: ClientInput<"trackEvent">["body"] = {
      userType: "anonymous",
      anonymousProfile: {
        id: anonymousId,
        email: demoEmail ?? undefined,
      },
      eventName: obj.eventName,
      properties,
    }
    delivered = await postTrackEvent({ body, throwOnFailure: obj.throwOnFailure })
  }

  // The API has stored and forwarded the lead, so the GA4 conversion can fire.
  // A swallowed delivery failure leaves `delivered` false and the conversion is
  // correctly not counted; a thrown failure never reaches this line.
  if (deferGA4UntilDelivered && delivered && !isAutomatedTest) {
    trackGA4DemoEvent(obj.eventName, ga4Properties)
  }
}
